import { type NumberValue, ScaleSymLog } from 'd3-scale';

import type {
  DateTimeFormatter,
  FormatterWithPrecision,
  StringFormatter,
} from '../../core/types/formatter.js';
import { adaptivelyReduceHorizontalTicks } from '../../core/utils/adaptively-reduce-horizontal-ticks.js';
import {
  getCategoricalTicks,
  getDateNumericalFormattedTicks,
  getFormattedTicks,
  type TickAxis,
} from '../../core/utils/formatter-by-ticks.js';
import type { ContinuousScale } from '../../core/utils/has-horizontal-label-collision.js';
import { isCategorical, isLinear, isLogarithmic } from '../types/guards.js';
import type {
  AnyXYChartScale,
  XYChartAxisInternal,
} from '../types/xy-chart-internals.js';

const MIN_TICK_COUNT = 2;

export function getTicksFormattedByConfig(
  config: XYChartAxisInternal | undefined,
  numTicks: number,
  scale: AnyXYChartScale | undefined,
  formatter:
    | FormatterWithPrecision
    | DateTimeFormatter
    | StringFormatter
    | undefined,
  availableWidth?: number,
) {
  const axisType = config?.type || '';

  if (isCategorical(axisType)) {
    return getCategoricalTicks(scale, formatter as StringFormatter | undefined);
  }

  const getTicksForCount = isLogarithmic(axisType)
    ? (count: number) =>
        getFormattedTicks(
          count,
          scale as ScaleSymLog<number, never>,
          undefined,
          formatter as FormatterWithPrecision | undefined,
        )
    : (count: number) =>
        getDateNumericalFormattedTicks(
          count,
          scale,
          isLinear(axisType) && !config?.isRelative ? config?.unit : undefined,
          formatter as FormatterWithPrecision | DateTimeFormatter | undefined,
        );

  const result = getTicksForCount(numTicks);

  if (
    isLinear(axisType) &&
    config &&
    'allowDecimals' in config &&
    config.allowDecimals === false
  ) {
    const integerIndices = result.ticks
      .map((t, i) => (Number.isInteger(Number(t)) ? i : -1))
      .filter((i) => i !== -1);

    if (integerIndices.length >= MIN_TICK_COUNT) {
      const filteredTicks = integerIndices.map((i) => result.ticks[i]);
      return {
        ticks: filteredTicks,
        formattedTicks: integerIndices.map((i) => result.formattedTicks[i]),
        numericTicks: filteredTicks.map(Number),
      };
    }
  }

  if (availableWidth === undefined || scale === undefined) {
    return result;
  }

  // When D3 returned fewer than the minimum (e.g. numTicks=1 on a narrow chart),
  // try once more with an explicit hint of MIN_TICK_COUNT before falling back.
  let initialResult = result;
  if (result.formattedTicks.length < MIN_TICK_COUNT) {
    const boosted = getTicksForCount(MIN_TICK_COUNT);
    if (boosted.formattedTicks.length >= MIN_TICK_COUNT) {
      initialResult = boosted;
    } else {
      return result;
    }
  }

  const continuousScale = scale as ContinuousScale;
  const rawDomain =
    typeof (scale as { domain?: unknown }).domain === 'function'
      ? (scale as { domain(): (number | Date)[] }).domain()
      : null;
  const domainBounds: [number, number] | undefined =
    rawDomain !== null && rawDomain.length >= 2
      ? [+rawDomain[0], +rawDomain[rawDomain.length - 1]]
      : undefined;

  const reduced = adaptivelyReduceHorizontalTicks(
    isLogarithmic(axisType)
      ? createLogMajorTickThinningBuilder(initialResult, MIN_TICK_COUNT)
      : createHalvingHintCandidateBuilder(
          initialResult,
          getTicksForCount,
          MIN_TICK_COUNT,
        ),
    continuousScale,
    { domainBounds },
  );

  return {
    ...reduced,
    numericTicks: reduced.rawTicks.map(Number),
  };
}

export type FormattedTickCandidate = TickAxis & {
  // `TickAxis.ticks` is typed as `string[]` for legacy reasons, but at runtime
  // `getTicks` returns the raw scale values (numbers/Dates) which satisfy
  // `NumberValue`. We cast at the boundary so the collision check can use them
  // as scale-position inputs.
  rawTicks: NumberValue[];
};

/**
 * Returns a candidate builder for `adaptivelyReduceHorizontalTicks` that is
 * tailored to logarithmic (symlog) x-axes.
 *
 * Unlike the halving-hint builder, D3's symlog tick generator returns the same
 * set of major ticks regardless of the hint once the hint is below the number
 * of decades in the domain. This builder therefore skips the hint entirely and
 * instead manually thins the major (non-empty-label) ticks on each attempt,
 * keeping the first and last decade visible and dropping every other one in
 * between. Minor ticks (empty-string labels) are preserved so that their tick
 * marks remain visible on the axis.
 */
function createLogMajorTickThinningBuilder(
  initialResult: TickAxis,
  minTickCount: number,
): (attempt: number) => FormattedTickCandidate | null {
  const allTicks = initialResult.ticks;
  const majorIndices = initialResult.formattedTicks.reduce<number[]>(
    (acc, label, i) => {
      if (label !== '') {
        acc.push(i);
      }
      return acc;
    },
    [],
  );

  return (attempt) => {
    if (attempt === 0) {
      return {
        ...initialResult,
        rawTicks: allTicks as unknown as NumberValue[],
      };
    }

    // Keep the first and last major tick; between them retain every 2^attempt-th.
    const step = Math.pow(2, attempt);
    const selectedMajorIndices = majorIndices.filter(
      (_, i) => i === 0 || i === majorIndices.length - 1 || i % step === 0,
    );

    if (selectedMajorIndices.length < minTickCount) {
      return null;
    }

    const selectedSet = new Set(selectedMajorIndices);
    const newFormattedTicks = initialResult.formattedTicks.map((label, i) => {
      if (label === '') {
        return ''; // minor tick — keep invisible
      }
      return selectedSet.has(i) ? label : ''; // deselected major tick
    });

    return {
      ...initialResult,
      formattedTicks: newFormattedTicks,
      rawTicks: allTicks as unknown as NumberValue[],
    };
  };
}

/**
 * Returns a stateful candidate builder for `adaptivelyReduceHorizontalTicks`.
 *
 * Attempt 0 returns `initialResult` directly. Each subsequent attempt halves
 * the d3 tick-count hint (floored to `minTickCount`); if d3's nice-snapping
 * returns the same count for a given hint, the next halving eventually forces
 * d3 to pick a coarser nice step. Exposed for unit testing — production
 * callers should pass the result directly to `adaptivelyReduceHorizontalTicks`.
 */
export function createHalvingHintCandidateBuilder(
  initialResult: TickAxis,
  getTicksForCount: (count: number) => TickAxis,
  minTickCount: number,
): (attempt: number) => FormattedTickCandidate {
  let hint = initialResult.formattedTicks.length;
  return (attempt) => {
    if (attempt === 0) {
      return {
        ...initialResult,
        rawTicks: initialResult.ticks as unknown as NumberValue[],
      };
    }
    hint = Math.max(Math.floor(hint / 2), minTickCount);
    const next = getTicksForCount(hint);
    return { ...next, rawTicks: next.ticks as unknown as NumberValue[] };
  };
}
